import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  ArrowRight,
  Clock,
  FileCheck,
  Eye,
  Bell,
  CheckCircle2,
  ClipboardList,
  MapPin,
  Camera,
  FileText,
  BarChart3,
  Shield,
  Globe,
  Users,
  Building2,
  QrCode,
  Handshake,
  Truck
} from 'lucide-react';

const EXPORTER_IMG = "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/tfxjwzbx_miobile.jpg";
const INSPECTOR_IMG = "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/exgj7zlv_access.jpg";
const REGULATOR_IMG = "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/3j46r8io_compliance.jpg";
const TRADE_IMG = "https://images.unsplash.com/photo-1761857989912-275b46ab8f2f?w=800";

const audiences = [
  {
    id: "exporters",
    title: "Exporters & Producers",
    subtitle: "Faster submissions, fewer delays",
    description: "Certisfy simplifies the certificate application process for agricultural exporters and producers. Submit applications, track status, and receive certificates—all through a single digital platform.",
    image: EXPORTER_IMG,
    benefits: [
      { icon: Clock, text: "Fast submissions with guided forms" },
      { icon: FileCheck, text: "Clear export requirements by commodity and destination" },
      { icon: Eye, text: "Real-time application status tracking" },
      { icon: Bell, text: "Instant notifications on approvals" },
      { icon: FileText, text: "Digital certificate delivery" }
    ],
    color: "green"
  },
  {
    id: "inspectors",
    title: "Inspectors",
    subtitle: "Streamlined workflows, better tools",
    description: "Purpose-built tools for inspection teams. Manage your queue, record findings, attach evidence, and submit assessments—all from one interface designed for efficiency.",
    image: INSPECTOR_IMG,
    benefits: [
      { icon: ClipboardList, text: "Queue-based assignment by office location" },
      { icon: MapPin, text: "Pool or individual inspection allocation" },
      { icon: Camera, text: "Attach photos and evidence directly" },
      { icon: CheckCircle2, text: "Standardized checklists and forms" },
      { icon: FileText, text: "Digital notes and findings submission" }
    ],
    color: "gold"
  },
  {
    id: "regulators",
    title: "Regulators & NPPO",
    subtitle: "National oversight and compliance",
    description: "Gain complete visibility into certification activities across all regions. Monitor compliance, generate reports, and ensure policy alignment with powerful administrative tools.",
    image: REGULATOR_IMG,
    benefits: [
      { icon: BarChart3, text: "National-level reporting dashboards" },
      { icon: Shield, text: "Compliance enforcement tools" },
      { icon: Globe, text: "Multi-region oversight capabilities" },
      { icon: Users, text: "User and role management" },
      { icon: Building2, text: "Policy configuration and alignment" }
    ],
    color: "green"
  },
  {
    id: "trade-partners",
    title: "Trade Partners",
    subtitle: "Trust and verification",
    description: "Verify certificate authenticity instantly. Certisfy certificates include security features that trade partners can validate, building trust in every shipment.",
    image: TRADE_IMG,
    benefits: [
      { icon: QrCode, text: "QR code verification" },
      { icon: Shield, text: "Watermark and security text validation" },
      { icon: CheckCircle2, text: "Consistent certificate formatting" },
      { icon: Handshake, text: "Trusted documentation standards" },
      { icon: Truck, text: "Smoother cross-border trade" }
    ],
    color: "gold"
  }
];

export default function SolutionsPage() {
  return (
    <div data-testid="solutions-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-green mb-4 inline-block">Solutions</span>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Built for every stakeholder in 
              <span className="text-[#1F7A3A]"> agricultural trade</span>
            </h1>
            <p className="text-lg text-slate-600">
              Whether you're an exporter applying for certificates, an inspector conducting assessments, 
              or a regulator overseeing compliance—Certisfy has purpose-built tools for your role.
            </p>
          </div>
        </div>
      </section>

      {/* Audience Sections */}
      {audiences.map((audience, index) => (
        <section 
          key={audience.id}
          id={audience.id}
          data-testid={`solution-${audience.id}`}
          className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <span className={`${audience.color === 'gold' ? 'badge-gold' : 'badge-green'} mb-4 inline-block`}>
                  {audience.title}
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  {audience.subtitle}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {audience.description}
                </p>
                
                <ul className="space-y-4 mb-8">
                  {audience.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        audience.color === 'gold' ? 'bg-[#FFFBEB] text-[#C8A24A]' : 'bg-[#F0FDF4] text-[#1F7A3A]'
                      }`}>
                        <benefit.icon className="w-5 h-5" />
                      </div>
                      <span className="text-slate-700 pt-2">{benefit.text}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact?demo=true" data-testid={`solution-cta-${audience.id}`}>
                  <Button className="bg-[#1F7A3A] text-white hover:bg-[#145A2A] rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl inline-flex items-center gap-2">
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="rounded-2xl overflow-hidden shadow-float">
                  <img 
                    src={audience.image} 
                    alt={audience.title}
                    className="w-full h-80 lg:h-96 object-cover"
                  />
                </div>
                {/* Decorative elements */}
                <div className={`absolute -bottom-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} w-24 h-24 rounded-2xl -z-10 ${
                  audience.color === 'gold' ? 'bg-[#C8A24A]/10' : 'bg-[#1F7A3A]/10'
                }`} />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <CTAStrip 
        title="Find your solution"
        subtitle="Let us show you how Certisfy can work for your specific role and requirements."
      />
    </div>
  );
}
